/**
 * IniciativasPage.tsx — Página "Iniciativas" (/iniciativas)
 * Lista Órbita e NECTARIS OS como iniciativas distintas do grupo.
 * Adaptada ao Claude Design (light + acentos escuros).
 * Hierarquia: Nectaris=grupo; NECTARIS OS e Órbita = produtos distintos.
 *
 * ORB-SITE-002: animações via data-reveal gerenciadas pelo useNectarisFx em SiteLayout.
 */

import { NectarisLogoMark } from "../atoms";

const INICIATIVAS = [
  {
    id: "orbita",
    code: "IN/01 · SISTEMA DE GESTÃO",
    name: "Órbita",
    tagline: "Gestão empresarial multi-tenant",
    desc: "Sistema de gestão e controle empresarial com módulos financeiros, custeio, fluxo de caixa e análise de margem. Projetado para empresas de médio e grande porte que precisam de inteligência operacional rastreável.",
    features: [
      "Controle financeiro consolidado",
      "Custeio por centro de custo",
      "Fluxo de caixa por blocos configuráveis",
      "DRE gerencial com drill-down",
      "Multi-tenant com white-label por cliente",
      "Auditabilidade ponta a ponta",
    ],
    status: "Em desenvolvimento",
    active: false,
    primary: true,
  },
  {
    id: "nectaris-os",
    code: "IN/02 · SHELL EXECUTIVO",
    name: "NECTARIS OS",
    tagline: "Camada de gestão executiva",
    desc: "Shell de inteligência executiva que consolida dados de ERP, CRM e planilhas num modelo canônico único. Entrega DRE gerencial, board pack e KPIs ao vivo — sobre o que a empresa já usa, sem trocar de sistema.",
    features: [
      "Modelo canônico de dados",
      "DRE Gerencial e Board Pack",
      "Indicadores ao vivo (D+1)",
      "Rastreabilidade total por número",
      "Integração com ERP, CRM e APIs",
      "Dashboard executivo configurável",
    ],
    status: "Em operação",
    active: true,
    primary: false,
  },
];

function IniciativasHeader() {
  return (
    <header
      className="ns-grupo-header"
      aria-labelledby="iniciativas-h1"
      style={{ background: "radial-gradient(1100px 640px at 78% 12%, #17334F 0%, #0B1B2B 58%)" }}
    >
      <div className="ns-wrap">
        <p className="ns-eyebrow ns-eyebrow--dark">iniciativas</p>
        <h1 id="iniciativas-h1" style={{ color: "#FFFFFF", maxWidth: "22ch" }}>
          As iniciativas do{" "}
          <span className="ns-accent-dark">Nectaris Group.</span>
        </h1>
        <p style={{ fontSize: "20px", lineHeight: 1.6, color: "rgba(230,239,244,.8)", maxWidth: "56ch", marginTop: "26px" }}>
          O grupo mantém produtos distintos para contextos distintos. Cada iniciativa tem
          escopo, entrada e posicionamento próprios — unidos pelo mesmo compromisso com
          rastreabilidade e precisão.
        </p>
      </div>
    </header>
  );
}

function IniciativasLista() {
  return (
    <section
      style={{ background: "#F4F6F8", padding: "100px 0", borderBottom: "1px solid #E2E8ED" }}
      aria-labelledby="lista-h2"
    >
      <div className="ns-wrap">
        <p className="ns-eyebrow">portfólio do grupo</p>
        <h2 id="lista-h2" data-reveal style={{ maxWidth: "22ch", marginBottom: "52px" }}>
          Produtos e plataformas
        </h2>

        <div
          data-reveal
          data-reveal-delay="120"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))",
            gap: "24px",
          }}
          role="list"
          aria-label="Iniciativas do grupo"
        >
          {INICIATIVAS.map((ini) => (
            <article
              key={ini.id}
              role="listitem"
              style={{
                background: ini.primary ? "#0B1B2B" : "#FFFFFF",
                border: `1px solid ${ini.primary ? "rgba(55,183,214,.3)" : "#E2E8ED"}`,
                borderRadius: "20px",
                padding: "42px 40px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
              aria-labelledby={`ini-${ini.id}-name`}
            >
              <p style={{
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "13px",
                letterSpacing: "0.18em",
                color: ini.primary ? "#37B7D6" : "#B8842D",
                margin: 0,
              }}>
                {ini.code}
              </p>
              <h3
                id={`ini-${ini.id}-name`}
                style={{
                  fontFamily: "Space Grotesk, sans-serif",
                  fontWeight: 700,
                  fontSize: "28px",
                  color: ini.primary ? "#FFFFFF" : "#0B1B2B",
                  margin: 0,
                }}
              >
                {ini.name}
              </h3>
              <p style={{
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "11px",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: ini.primary ? "#E0A43B" : "#117E99",
                margin: 0,
              }}>
                {ini.tagline}
              </p>
              <p style={{
                fontSize: "16px",
                lineHeight: 1.65,
                color: ini.primary ? "rgba(230,239,244,.78)" : "#45525C",
                margin: 0,
              }}>
                {ini.desc}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" }}>
                {ini.features.map((feat) => (
                  <li key={feat} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ color: ini.primary ? "#E0A43B" : "#117E99", fontSize: "13px" }} aria-hidden="true">✓</span>
                    <span style={{ fontSize: "15px", color: ini.primary ? "rgba(230,239,244,.72)" : "#45525C" }}>
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "8px",
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "12px",
                letterSpacing: "0.12em",
                color: ini.active ? "#45C4B0" : (ini.primary ? "rgba(230,239,244,.5)" : "#6B7683"),
              }}>
                <span style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: ini.active ? "#45C4B0" : (ini.primary ? "rgba(230,239,244,.3)" : "#6B7683"),
                  flexShrink: 0,
                }} aria-hidden="true" />
                {ini.status}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function IniciativasCta() {
  return (
    <section
      className="ns-cta-section"
      aria-label="Chamada para contato sobre iniciativas"
    >
      <div className="ns-cta-section__inner">
        <h2 className="ns-cta-section__h2" data-reveal>
          Qual iniciativa faz sentido para você?{" "}
          <span className="ns-accent-dark">Vamos conversar.</span>
        </h2>
        <a href="/contato" className="ns-cta-section__btn" data-reveal data-reveal-delay="120">
          Falar com o grupo
        </a>
      </div>

      <footer className="ns-footer" aria-label="Rodapé Nectaris">
        <div className="ns-footer__inner">
          <a href="/" className="ns-footer__brand" aria-label="Nectaris — início">
            <NectarisLogoMark size={30} variant="dark" />
            <span className="ns-footer__wordmark">NECTARIS</span>
          </a>
          <span className="ns-footer__copy">© 2026 NECTARIS · BRASIL</span>
        </div>
      </footer>
    </section>
  );
}

export function IniciativasPage() {
  return (
    <>
      <IniciativasHeader />
      <IniciativasLista />
      <IniciativasCta />
    </>
  );
}
